import React, { useEffect, useState } from 'react'
import './TransactionsCust.css'
import { useLocation, useHistory } from 'react-router-dom'
import { BiChevronLeft, BiMenu } from 'react-icons/bi'
import Axios from 'axios'
import ReactTooltip from 'react-tooltip'
import Header from '../../layouts/Header'
import SideNavCust from '../../layouts/SideNavCust'
import SideNav from '../../layouts/SideNav'
import ClipLoader from 'react-spinners/ClipLoader'
import { IoCheckmarkCircleOutline } from 'react-icons/io5'
import { API_URL } from '../../config'

const url = `${API_URL}/apps/kscanner/webservices.php`

function formatToZAR(amount) {
  const formatted = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount || 0)
  return formatted.replace(/\s/g, '')
}

/* general -> General, vip -> VIP, vvip -> VVIP */
const UPPERCASE_TYPES = ['vip', 'vvip']

function formatTypeLabel(name) {
  const s = String(name ?? '').trim()
  if (!s) return '-'
  if (UPPERCASE_TYPES.includes(s.toLowerCase())) return s.toUpperCase()
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

/* "General x3, VIP x2" -> [{ label: 'General', qty: 3 }, { label: 'VIP', qty: 2 }]
   A plain label with no count (organiser view) gives qty: null. */
function parseTicketTypes(raw) {
  return String(raw ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => {
      const m = part.match(/^(.*?)\s*[xX×]\s*(\d+)$/)
      return {
        label: formatTypeLabel(m ? m[1] : part),
        qty: m ? Number(m[2]) : null,
      }
    })
}

const STATS = [
  { key: 'complete', label: 'Completed' },
  { key: 'pending', label: 'Pending' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'abandoned', label: 'Abandoned' },
  { key: 'refunds', label: 'Refunds' },
]

const STATUS_TABS = [
  ['ALL', 'All'],
  ['COMPLETE', 'Complete'],
  ['PENDING', 'Pending'],
  ['CANCELLED', 'Cancelled'],
  ['ABANDONED', 'Abandoned'],
]

const LedgerStrip = ({ values }) => {
  // proportion bar covers the four payment states; refunds shown as a figure only
  const parts = ['complete', 'pending', 'cancelled', 'abandoned']
  const total = parts.reduce((s, k) => s + (Number(values[k]) || 0), 0)

  return (
    <section className="kx-tx-ledger" aria-label="Transaction totals">
      <div className="kx-tx-ledger__top">
        <div>
          <small>Total transaction value</small>
          <strong>{formatToZAR(total)}</strong>
        </div>
        <span className="kx-tx-ledger__hint">Share by status</span>
      </div>

      <div className="kx-tx-bar" role="img" aria-label="Share of transaction value by status">
        {total === 0 ? (
          <span className="kx-tx-seg kx-tx-seg--empty" style={{ width: '100%' }} />
        ) : (
          parts.map((k) => {
            const pct = ((Number(values[k]) || 0) / total) * 100
            return pct > 0 ? (
              <span key={k} className={`kx-tx-seg kx-tx-seg--${k}`} style={{ width: `${pct}%` }} />
            ) : null
          })
        )}
      </div>

      <dl className="kx-tx-stats">
        {STATS.map((s) => (
          <div key={s.key} className="kx-tx-stat">
            <dt>
              <i className={`kx-tx-dot kx-tx-seg--${s.key}`} />
              {s.label}
            </dt>
            <dd>{formatToZAR(values[s.key])}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

const EventRow = ({ transact, account_type, handleResend, resendingId }) => {
  const isCustomer = account_type === 'customer'
  const pay = transact.payment_status.toLowerCase()
  const eventName = transact.event_name.trim()
  const types = parseTicketTypes(transact.ticket_type)
  const resendDisabled =
    pay !== 'complete' ||
    transact.ticket_resent_at != null ||
    resendingId === transact.transaction_id ||
    transact.refund_status === 'approved'

  const refundClass =
    transact.refund_status === 'pending'
      ? 'kx-tx-pill kx-tx-pill--pending'
      : transact.refund_status === 'approved'
        ? 'kx-tx-pill kx-tx-pill--complete'
        : 'kx-tx-pill kx-tx-pill--cancelled'

  return (
    <tr>
      <td
        data-label="ID"
        className="kx-tx-mono"
        data-tip={transact.transaction_id}
        data-place="right"
      >
        …{transact.transaction_id.slice(-6)}
      </td>
      <td data-label="Date">{transact.date ?? transact.created_at}</td>
      <td data-label="Event" className="kx-tx-event" data-tip={eventName} data-place="right">
        {eventName.length > 22 ? eventName.slice(0, 22) + '…' : eventName}
      </td>
      {!isCustomer && <td data-label="Buyer">{transact.name_first}</td>}
      <td data-label="Type">
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px' }}>
          {types.map((t, i) => (
            <span
              key={`${t.label}-${i}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
            >
              <span>{t.label}</span>
              {t.qty !== null && types.length > 1 && (
                <span
                  style={{
                    display: 'inline-block',
                    minWidth: 24,
                    padding: '0 8px',
                    borderRadius: 999,
                    background: '#fdecee',
                    border: '1px solid #f6c9ce',
                    color: '#d91f2d',
                    fontSize: 11,
                    fontWeight: 700,
                    lineHeight: '18px',
                    textAlign: 'center',
                  }}
                >
                  ×{t.qty}
                </span>
              )}
            </span>
          ))}
        </div>
      </td>
      z
      <td data-label="Admissions" className="kx-tx-num">
        {transact.admissions}
      </td>
      <td data-label="Amount" className="kx-tx-num kx-tx-amount">
        {formatToZAR(transact.amount_gross)}
      </td>
      <td data-label="Status">
        <span className={`kx-tx-pill kx-tx-pill--${pay}`}>{pay}</span>
      </td>
      <td data-label="Refund status">
        {transact.refund_status !== null ? (
          <span className={refundClass}>{transact.refund_status}</span>
        ) : (
          <span className="kx-tx-muted">None</span>
        )}
      </td>
      <td data-label="Refunded" className="kx-tx-num">
        {formatToZAR(transact.amount_refunded)}
      </td>
      {isCustomer && (
        <td data-label="Ticket" className="kx-tx-action-cell">
          <button
            type="button"
            className={`kx-tx-resend ${transact.ticket_resent_at != null ? 'is-sent' : ''}`}
            disabled={resendDisabled}
            data-tip={
              transact.ticket_resent_at != null
                ? 'Ticket already resent at ' + transact.ticket_resent_at
                : 'Resend ticket'
            }
            data-place="top"
            onClick={() => !resendDisabled && handleResend(transact.transaction_id)}
          >
            {resendingId === transact.transaction_id ? (
              <ClipLoader size={14} color="#d91f2d" />
            ) : transact.ticket_resent_at != null ? (
              <>
                <IoCheckmarkCircleOutline /> Resent
              </>
            ) : (
              'Resend ticket'
            )}
          </button>
        </td>
      )}
    </tr>
  )
}

const TransactionsCust = () => {
  const location = useLocation()
  const history = useHistory()
  const account_type = localStorage.getItem('accounttype')
  const isCustomer = account_type === 'customer'
  const queryParams = new URLSearchParams(location.search)
  const eventId = queryParams.get('q_id') || 0

  const [statusFilter, setStatusFilter] = useState('ALL')
  const [sortBy, setSortBy] = useState('DATE_DESC')

  const [pendingTransact, setPendingTransact] = useState(0)
  const [completeTransact, setCompleteTransact] = useState(0)
  const [cancelledTransact, setCancelledTransact] = useState(0)
  const [abandonedTransact, setAbandonedTransact] = useState(0)
  const [refundedAmt, setRefundedAmt] = useState(0)
  const [transactionsList, setTransactionsList] = useState([])
  const [loader, setLoader] = useState(true)
  const [resendStatus, setResendStatus] = useState(null) // 'success' | 'error' | null
  const [resendReponse, setResendReponse] = useState(null)
  const [resendingId, setResendingId] = useState(null)
  const [count, setCount] = useState(0)

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  const isMobile = window.innerWidth <= 768
  const [isMob, setIsMob] = useState(isMobile)
  const [collapsed, setCollapsed] = useState(isMobile)

  const updateIsMobile = () => {
    const mobile = window.innerWidth <= 768
    setIsMob(mobile)
    setCollapsed((prev) => (mobile ? true : prev))
  }

  const handleResend = async (transaction_id) => {
    setResendingId(transaction_id)
    const formData = new FormData()
    formData.append('transaction_id', transaction_id)
    formData.append('query', 'RESEND-TICKET')

    try {
      const response = await Axios({
        url: url,
        method: 'POST',
        data: formData,
        withCredentials: true,
      })

      const status = response.data?.status
      setResendReponse(response.data?.message)
      setResendStatus(status === 'success' ? 'success' : 'error')
    } catch (error) {
      console.log('Error occurred:', error)
      setResendStatus('error')
    } finally {
      setCount(count + 1)
      setTimeout(() => {
        setResendStatus(null)
        setResendingId(null)
      }, 3000)
    }
  }

  const fetchongoingEventsData = async () => {
    Axios.get(`${url}?query=TRANSACTION-AMOUNTS&event_id=${eventId}`, {
      withCredentials: true,
    })
      .then((response) => {
        setPendingTransact(response.data.pendingAmount)
        setCompleteTransact(response.data.completedAmount)
        setCancelledTransact(response.data.cancelledAmount)
        setAbandonedTransact(response.data.abandonedTransact)
        setRefundedAmt(response.data.refundedAmt)
      })
      .catch((error) => {
        console.error('Error fetching dashboard data:', error)
      })
  }

  const fetchTransactions = async () => {
    Axios.get(`${url}?query=TRANSACTIONS-LIST&event_id=${eventId}`, {
      withCredentials: true,
    })
      .then((response) => {
        setLoader(false)
        if (response.data.status === 'success') {
          setTransactionsList(response.data.transactions)
        } else {
          setTransactionsList([])
        }
      })
      .catch((error) => {
        setLoader(false)
        setTransactionsList([])
        console.error('Error fetching dashboard data:', error)
      })
  }

  useEffect(() => {
    fetchongoingEventsData()
    fetchTransactions()
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    return () => window.removeEventListener('resize', updateIsMobile)
  }, [eventId, count])

  useEffect(() => {
    setCurrentPage(1)
  }, [statusFilter, sortBy])

  useEffect(() => {
    ReactTooltip.rebuild()
  })

  const handleToggle = () => setCollapsed((prev) => !prev)

  const filteredTransactions = transactionsList.filter((tx) =>
    statusFilter === 'ALL' ? true : tx.payment_status.toUpperCase() === statusFilter,
  )

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    switch (sortBy) {
      case 'DATE_ASC':
        return new Date(a.created_at) - new Date(b.created_at)
      case 'DATE_DESC':
        return new Date(b.created_at) - new Date(a.created_at)
      case 'AMOUNT_ASC':
        return a.amount_gross - b.amount_gross
      case 'AMOUNT_DESC':
        return b.amount_gross - a.amount_gross
      case 'STATUS_ASC':
        return a.payment_status.localeCompare(b.payment_status)
      case 'STATUS_DESC':
        return b.payment_status.localeCompare(a.payment_status)
      default:
        return 0
    }
  })

  const tabCounts = STATUS_TABS.reduce((acc, [id]) => {
    acc[id] =
      id === 'ALL'
        ? transactionsList.length
        : transactionsList.filter((tx) => tx.payment_status.toUpperCase() === id).length
    return acc
  }, {})

  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentTransactions = sortedTransactions.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.max(1, Math.ceil(sortedTransactions.length / itemsPerPage))

  return (
    <div className="kx-tx-page">
      <Header />
      {isCustomer ? (
        <SideNavCust collapsed={collapsed} onToggle={handleToggle} />
      ) : (
        <SideNav collapsed={collapsed} onToggle={handleToggle} />
      )}

      {isMob && !collapsed && (
        <div className="kx-tx-overlay" onClick={handleToggle} aria-hidden="true" />
      )}

      <main className={`kx-tx-main ${collapsed ? 'kx-tx-main--collapsed' : ''}`}>
        {isMob && collapsed && (
          <button
            type="button"
            className="kx-tx-menu"
            onClick={handleToggle}
            aria-label="Open menu"
          >
            <BiMenu size={24} />
          </button>
        )}

        <div className="kx-tx">
          <header className="kx-tx-head">
            <div>
              <span className="kx-tx-eyebrow">
                <i /> Payments
              </span>
              <h1>Transactions</h1>
              <p>
                {sortedTransactions.length}{' '}
                {sortedTransactions.length === 1 ? 'transaction' : 'transactions'}
                {statusFilter !== 'ALL' ? ` · ${statusFilter.toLowerCase()}` : ''}
              </p>
            </div>
            <button type="button" className="kx-tx-back" onClick={() => history.goBack()}>
              <BiChevronLeft size={20} /> Back
            </button>
          </header>

          <LedgerStrip
            values={{
              complete: completeTransact,
              pending: pendingTransact,
              cancelled: cancelledTransact,
              abandoned: abandonedTransact,
              refunds: refundedAmt,
            }}
          />

          <section className="kx-tx-panel">
            <div className="kx-tx-bar-controls">
              <div className="kx-tx-tabs" role="tablist" aria-label="Filter by status">
                {STATUS_TABS.map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={statusFilter === id}
                    className={statusFilter === id ? 'is-active' : ''}
                    onClick={() => setStatusFilter(id)}
                  >
                    {label}
                    <b>{tabCounts[id]}</b>
                  </button>
                ))}
              </div>

              <label className="kx-tx-field">
                <span>Sort by</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                  <option value="DATE_DESC">Date (newest)</option>
                  <option value="DATE_ASC">Date (oldest)</option>
                  <option value="AMOUNT_DESC">Amount (high to low)</option>
                  <option value="AMOUNT_ASC">Amount (low to high)</option>
                  <option value="STATUS_ASC">Status (A to Z)</option>
                  <option value="STATUS_DESC">Status (Z to A)</option>
                </select>
              </label>
            </div>

            <div className="kx-tx-table-wrap">
              <table className="kx-tx-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Event</th>
                    {!isCustomer && <th>Buyer</th>}
                    <th>Type</th>
                    <th className="kx-tx-num">Admissions</th>
                    <th className="kx-tx-num">Amount</th>
                    <th>Status</th>
                    <th>Refund status</th>
                    <th className="kx-tx-num">Refunded</th>
                    {isCustomer && <th>Ticket</th>}
                  </tr>
                </thead>
                <tbody>
                  {loader ? (
                    <tr className="kx-tx-state">
                      <td colSpan={10}>
                        <ClipLoader color="#d91f2d" loading size={22} />
                      </td>
                    </tr>
                  ) : currentTransactions.length === 0 ? (
                    <tr className="kx-tx-state">
                      <td colSpan={10}>
                        <strong>
                          {statusFilter === 'ALL'
                            ? 'No transactions yet.'
                            : 'No matching transactions'}
                        </strong>
                        {statusFilter !== 'ALL' && (
                          <p>No transactions match this status. Choose All to see everything.</p>
                        )}
                      </td>
                    </tr>
                  ) : (
                    currentTransactions.map((transact) => (
                      <EventRow
                        key={transact.transaction_id}
                        transact={transact}
                        account_type={account_type}
                        handleResend={handleResend}
                        resendingId={resendingId}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {sortedTransactions.length > itemsPerPage && (
              <nav className="kx-tx-pager" aria-label="Pagination">
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </nav>
            )}
          </section>
        </div>
      </main>

      <ReactTooltip />
      {resendStatus && (
        <div className={`kx-tx-toast ${resendStatus}`} role="status">
          {resendReponse}
        </div>
      )}
    </div>
  )
}

export default TransactionsCust
